import { useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { PencilIcon, UploadIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, Row, Text } from "@/components";
import { CompanyLogoCropDialog } from "@/components/companies/profile";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useUpdateSetCompanyLogo } from "@/hooks/updateSets/useUpdateSetCompanyLogo";
import { documentLogoSchema } from "@/lib/schemas";
import { cn } from "@/lib/utils";

const logoSchema = z.object({ file: documentLogoSchema });

export const CompanyLogoUploadTile = ({
  companyName,
  logoUrl,
  hasLogo,
  isSmallLogo = false,
}: {
  companyName: string;
  logoUrl?: string | null;
  hasLogo: boolean;
  isSmallLogo?: boolean;
}) => {
  /* Database CRUD Operations */
  // Update
  const {
    mutate: updateLogo,
    isPending,
    isError,
    reset,
  } = useUpdateSetCompanyLogo(isSmallLogo);

  /* React Hooks */
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileToCrop, setFileToCrop] = useState<File | null>(null);
  const form = useForm<z.infer<typeof logoSchema>>({
    resolver: zodResolver(logoSchema),
  });
  const label = isSmallLogo ? "Small logo" : "Logo";

  /* Handlers */
  const handleChooseFile = () => {
    reset();
    inputRef.current?.click();
  };
  const handleFileSelected = ({ file }: z.infer<typeof logoSchema>) => {
    setFileToCrop(file);
  };
  const handleClose = () => {
    setFileToCrop(null);
    form.reset();
    reset();
  };
  const handleUpload = (file: File) => {
    updateLogo(
      { file, companyName },
      {
        onSuccess: () => {
          setFileToCrop(null);
          form.reset();
        },
      },
    );
  };

  /* Render */
  const render = () => (
    <Column className={cn("items-center gap-2", isSmallLogo ? "w-16" : "w-24")}>
      <Row className="h-24 items-end">
        <Button
          type="button"
          variant="outline"
          disabled={isPending}
          title={`${hasLogo ? "Replace" : "Upload"} ${label.toLowerCase()}`}
          onClick={handleChooseFile}
          className={cn(
            "group relative shrink-0 overflow-hidden rounded-md bg-slate-50 p-0 hover:border-primary-400 hover:bg-slate-100",
            isSmallLogo ? "h-16 w-16" : "h-24 w-24",
            !hasLogo && "border-dashed",
          )}
        >
          {logoUrl ? (
            <>
              <img
                src={logoUrl}
                alt={`${companyName} ${label.toLowerCase()}`}
                className="h-full w-full object-contain"
              />
              <Column className="absolute bottom-1 right-1 rounded bg-white p-1 opacity-0 shadow-sm group-hover:opacity-100">
                <PencilIcon className="h-3 w-3" />
              </Column>
            </>
          ) : (
            <Column className="items-center gap-1">
              <UploadIcon className="h-5 w-5 text-slate-400" />
              <Text className="text-xs font-normal text-slate-500">Upload</Text>
            </Column>
          )}
        </Button>
      </Row>
      <Text className="text-xs font-medium text-slate-600">{label}</Text>
      <Form {...form}>
        <FormField
          control={form.control}
          name="file"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  ref={inputRef}
                  className="hidden"
                  type="file"
                  accept=".png,.jpg,.jpeg"
                  onChange={(event) => {
                    field.onChange(event.target.files?.[0]);
                    void form.handleSubmit(handleFileSelected)();
                    event.target.value = "";
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Form>
      {fileToCrop ? (
        <CompanyLogoCropDialog
          file={fileToCrop}
          isSmallLogo={isSmallLogo}
          isPending={isPending}
          isError={isError}
          onSave={handleUpload}
          onClose={handleClose}
        />
      ) : null}
    </Column>
  );
  return render();
};
